import axios from 'axios';
import Vue from 'vue';
import store from '@/store';
import configApp from '@/utils/appConfig';
import router from "@/router";
import qs from 'qs'
import { featureAccess } from "@/utils/accesses"
import { endSession, SESSION_END_REASONS } from '@/utils/session'

// A 401 from one of these is an answer about the credentials just offered, not a
// verdict on the session: signing in with the wrong password must show the form its
// error, not throw the user out of a session they never had.
const AUTH_ENDPOINTS = [
  'users/login',
  'users/register',
  'users/logout',
  'users/mfa/verify',
  'users/mfa/resend',
  'users/update-password',
  'users/reset-token',
]

const isAuthEndpoint = url => AUTH_ENDPOINTS.some(endpoint => (url || '').includes(endpoint))

export function resolveEssentialBaseUrl(config) {
  const baseUrl = config.baseURL ||
    (store.state.analytics?.activeCoinData?.baseUrl ?? store.state.analytics.coinData.baseUrl) ||
    configApp.VUE_APP_API_BASE_URL

  if (!baseUrl?.includes('essential-api-')) {
    return { baseUrl }
  }

  const essentialCoin = store.state.analytics.essentialCurrencyList?.find(({ baseUrl: coinBaseUrl }) => {
    return coinBaseUrl === baseUrl
  })

  if (!essentialCoin) {
    console.warn('Essential chain not found for baseUrl:', baseUrl)
    return { baseUrl }
  }

  const commonBaseUrl = baseUrl.replace(/-([^-/]+)$/, '')

  return {
    baseUrl: commonBaseUrl,
    key: essentialCoin.key
  }
}

const apiClient = axios.create();

let cancelTokenSource = axios.CancelToken.source(); // Variable to hold the cancel token source

apiClient.interceptors.request.use(config => {
  // List of routes for which requests should not be canceled
  const routesToExcludeCancellation = ['txBrowserIO', 'reset-token', 'entity'];
  const shouldExcludeCancellation = routesToExcludeCancellation.some(keyword =>
    config.url.includes(keyword)
  );

  config.paramsSerializer = p => {
    return qs.stringify(p, {arrayFormat: 'indices'})
  }

  if (featureAccess('UNIFIED_ESSENTIAL_BASEURL')) {
    const { baseUrl, key } = resolveEssentialBaseUrl(config)

    config.baseURL = baseUrl

    if (key !== undefined) {
      config.params = {
        ...config.params,
        chain: key
      }
    }
  } else {
    config.baseURL = config.baseURL ||
      (store.state.analytics?.activeCoinData?.baseUrl ?? store.state.analytics.coinData.baseUrl) ||
      configApp.VUE_APP_API_BASE_URL
  }

  // Generate a unique request key based on the request URL and method
  const requestKey = `${config.baseURL}_${config.url}_${config.method}_${JSON.stringify(config.params)}`

  // Cancel previous request (if any) with the same key
  if (cancelTokenSource && cancelTokenSource[requestKey] && router.currentRoute.name === 'analytics' && !shouldExcludeCancellation) {
    cancelTokenSource[requestKey].cancel('Request canceled');
  }

  if (router.currentRoute.name === 'analytics' && !shouldExcludeCancellation) {
    cancelTokenSource[requestKey] = axios.CancelToken.source(); // Create a new cancel token source for the request
    config.cancelToken = cancelTokenSource[requestKey].token; // Set the cancel token on the request config
  }

  const token = localStorage.getItem('token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
}, error => error);

let refreshTokenPromise

apiClient.interceptors.response.use(
    res => {
      if (res?.config?.url === 'users/me' && !res?.data?.success) {
          Vue.toasted.global.error({ message: `${res?.data?.data?.message}` })
          endSession(SESSION_END_REASONS.denied)
      }
      if (res?.data?.data?.status === 422 && router.currentRoute.name !== 'login')  {
          localStorage.setItem('lastRoute', router.currentRoute.name)
      }
      if (res?.data?.data?.status === 501) {
        Vue.toasted.global.info({ message: `This feature has not been implemented yet for selected blockchain - ${store.state.analytics.coinData.label}` })
        // throw new Error('Not implemented')
      }

      if (res.data && !res.data.status && res.data.data === null) {
        Vue.toasted.global.error({ message: 'API REQUEST ERROR' })
        throw new Error('API REQUEST ERROR')
      }
      // Handle response data
      return res.data;
    },
    async error => {
      if (error?.message === 'Request canceled') {
        return {
          success: false,
          canceled: true,
          data: null
        }
      }
      if (error?.response?.status === 403 && error?.config?.url === 'users/me') {
          Vue.toasted.global.error({ message: `${error?.response?.data?.data?.message || 'Access denied'}` })
          endSession(SESSION_END_REASONS.denied)
          // Throwing, where this used to `return`: a bare return resolves the
          // interceptor with undefined, so every `const { data } = await api…`
          // caller died on a TypeError instead of seeing the error.
          throw error
      }
      if (error?.response?.status === 401 && !isAuthEndpoint(error?.config?.url)) {
          const refreshToken = localStorage.getItem('refreshToken')
          localStorage.removeItem('token')
          localStorage.setItem('lastRoute', router.currentRoute.name)

          if (refreshToken && refreshToken !== 'undefined') {
              if (!refreshTokenPromise) {
                  refreshTokenPromise = store.dispatch('user/resetToken', { refreshToken })
                      .then(() => {
                          const fullPath = localStorage.getItem('to.fullPath') || router.currentRoute.name

                          return router.push(fullPath)
                      })
                      .catch(() => {
                          // Under a bulk invalidation the refresh cannot succeed. The
                          // dead refresh token goes with the rest of the session, which
                          // is what used to leave every later 401 calling reset-token
                          // with it.
                          endSession(SESSION_END_REASONS.expired)
                      })
                      // The latch was only ever released on success, so one failed
                      // refresh wedged every later 401 into silence.
                      .finally(() => {
                          refreshTokenPromise = undefined
                      })
              }
          } else {
              endSession(SESSION_END_REASONS.expired)
          }
      }
      // Handle response error
      throw error;
    }
);

export function cancelRequest(requestKey) {
  if (cancelTokenSource && cancelTokenSource[requestKey]) {
    cancelTokenSource[requestKey].cancel('Request canceled');
    delete cancelTokenSource[requestKey];
  }
}

export default apiClient;
